import { CircleNotchIcon, WarningCircleIcon, XIcon } from '@phosphor-icons/react';
import {
  FilterComposer,
  type FilterComposerProps,
} from '@voll-intelligence/ui-kit/componentes/filter_composer';
import type { AirportSide } from '../filter_logic.js';
import type { FilterChip } from '../filter_chips_state.js';
import type { FlightFilterChipsCopy, FlightFilterNote } from './filter_chips_copy.js';
import { fmtMoneyWhole } from '../flight_format.js';
import './filter_chips.css';

export interface FlightFilterChipNames {
  airline: (code: string) => string;
  airport: (iata: string) => string;
  /** Em que ponta da perna o aeroporto aparece nas ofertas em cena — decide "Saindo de" ×
   *  "Chegando em" (o fio não sabe: `airports` filtra qualquer ponta). O segundo argumento é a
   *  perna DO CHIP (R2-D3+#3495 rodada 2): `undefined` ⇒ perna ativa. Ausente ⇒ "Saindo de". */
  airportSide?: (iata: string, leg?: number) => AirportSide;
  currency?: string;
  /** Nome da perna do chip (R2-D3): `undefined` ⇒ perna ativa, sem prefixo no rótulo. */
  legLabel?: (leg: number) => string | undefined;
}

export interface FlightFilterChipsProps {
  chips: FilterChip[];
  note?: FlightFilterNote;
  /** Interpolação da nota (a `cabin` de `note.cabin`, R2-D4). */
  noteValues?: Readonly<Record<string, string>>;
  /** Um chip pendente em voo — o composer trava até a resposta. */
  busy: boolean;
  /** Explorer congelado/readOnly. */
  disabled: boolean;
  names: FlightFilterChipNames;
  copy: FlightFilterChipsCopy;
  /** O ditado do host; sem ele o mic não é desenhado, nem como vitrine. */
  dictation?: FilterComposerProps['dictation'];
  onSubmit: (text: string) => void;
  onRemove: (id: string) => void;
}

const list = (codes: string[], name: (code: string) => string): string =>
  codes.map(name).join(', ');

function windowLabel(
  copy: FlightFilterChipsCopy,
  axis: 'arrive' | 'depart',
  window: { start?: string; end?: string },
): string {
  const time = copy.time;
  if (window.start && window.end)
    return (axis === 'arrive' ? time.arriveBetween : time.departBetween)(window.start, window.end);
  if (window.end) return (axis === 'arrive' ? time.arriveBefore : time.departBefore)(window.end);
  return (axis === 'arrive' ? time.arriveAfter : time.departAfter)(window.start ?? '');
}

/**
 * Uma regra por campo do patch, na ORDEM EXATA do brief — `FlightFilterPatchSchema` é
 * `looseObject` sem "exatamente um campo" (D13), então um patch composto é plausível
 * ("voo direto sem escala no Panamá" seta `stops` E `avoidAirports` juntos), e a ORDEM
 * da lista é o que decide qual rótulo vence, não a ordem de declaração de um `if` chain.
 * `chipLabel` vira só um laço que tenta cada regra e para na primeira que bate — é isso
 * que mantém `complexity`/`sonarjs/cognitive-complexity` sob o teto sem reordenar nada.
 */
type PatchLabelRule = (
  copy: FlightFilterChipsCopy,
  names: FlightFilterChipNames,
  patch: NonNullable<FilterChip['patch']>,
) => string | undefined;

const PATCH_LABEL_RULES: readonly PatchLabelRule[] = [
  (copy, names, patch) => {
    if (!patch.airlines) return undefined;
    const name = list(patch.airlines, names.airline);
    return patch.airlinesExclude ? copy.airline.exclude(name) : copy.airline.only(name);
  },
  (copy, _names, patch) => (patch.stops ? copy.stops[patch.stops] : undefined),
  (copy, _names, patch) =>
    patch.arrivalWindow ? windowLabel(copy, 'arrive', patch.arrivalWindow) : undefined,
  (copy, _names, patch) =>
    patch.departureWindow ? windowLabel(copy, 'depart', patch.departureWindow) : undefined,
  (copy, names, patch) => {
    if (patch.maxPrice === undefined && patch.minPrice === undefined) return undefined;
    const money = (amount: number) =>
      names.currency ? fmtMoneyWhole({ amount, currency: names.currency }) : String(amount);
    if (patch.minPrice !== undefined && patch.maxPrice !== undefined)
      return copy.price.between(money(patch.minPrice), money(patch.maxPrice));
    if (patch.minPrice !== undefined) return copy.price.min(money(patch.minPrice));
    return copy.price.max(money(patch.maxPrice ?? 0));
  },
  (copy, _names, patch) => {
    if (patch.withinPolicyOnly === undefined) return undefined;
    return patch.withinPolicyOnly ? copy.policy.only : copy.policy.release;
  },
  (copy, names, patch) =>
    patch.avoidAirports ? copy.via.avoid(list(patch.avoidAirports, names.airport)) : undefined,
  (copy, names, patch) =>
    patch.viaAirports ? copy.via.through(list(patch.viaAirports, names.airport)) : undefined,
  (copy, names, patch) => {
    if (!patch.airports) return undefined;
    const place = list(patch.airports, names.airport);
    if (patch.airportsExclude) return copy.airport.exclude(place);
    const known = new Set(
      patch.airports
        .map((iata) => names.airportSide?.(iata) ?? 'from')
        .filter((side) => side !== 'unknown'),
    );
    if (known.size === 0 || (known.size === 1 && known.has('from')))
      return copy.airport.from(place);
    if (known.size === 1 && known.has('to')) return copy.airport.to(place);
    return copy.airport.only(place);
  },
  (copy, _names, patch) => (patch.sort ? copy.sort[patch.sort] : undefined),
];

/** O rótulo NASCE do patch (D8): o modelo nunca escreve copy de UI. A ordem de
 *  `PATCH_LABEL_RULES` É a precedência do brief — `chipLabel` só percorre a lista e para
 *  no primeiro rótulo definido, o que mantém a complexidade sob o teto sem tocar na ordem.
 *  Um chip de OUTRA perna (R2-D3) ganha o prefixo "Volta · " etc. — `names.legLabel`
 *  devolve `undefined` para a perna ativa, então o rótulo segue sem prefixo.
 *
 *  `names.airportSide` é derivado das ofertas da perna ATIVA (#3495 rodada 2): um chip de
 *  aeroporto de OUTRA perna precisa da ponta DAQUELA perna, não da ativa — sem escopar, um chip
 *  de volta deferred lia a ponta da ida e "Chegando em GRU" virava "Saindo de GRU". As regras em
 *  `PATCH_LABEL_RULES` continuam chamando `names.airportSide?.(iata)` sem saber de perna nenhuma;
 *  quem resolve é este wrapper, que fecha o `leg` do chip antes de entrar na lista de regras. */
export function chipLabel(
  chip: FilterChip,
  copy: FlightFilterChipsCopy,
  names: FlightFilterChipNames,
): string {
  if (chip.status === 'pending') return chip.text;
  const side = names.airportSide;
  const scoped: FlightFilterChipNames =
    side && chip.leg !== undefined
      ? { ...names, airportSide: (iata) => side(iata, chip.leg) }
      : names;
  const label = baseLabel(chip, copy, scoped);
  const leg = chip.leg === undefined ? undefined : names.legLabel?.(chip.leg);
  return leg ? copy.legPrefixed(leg, label) : label;
}

function baseLabel(
  chip: FilterChip,
  copy: FlightFilterChipsCopy,
  names: FlightFilterChipNames,
): string {
  const patch = chip.patch;
  if (!patch) return copy.axis[chip.axis];
  for (const rule of PATCH_LABEL_RULES) {
    const label = rule(copy, names, patch);
    if (label !== undefined) return label;
  }
  return copy.axis[chip.axis];
}

function noteText(
  copy: FlightFilterChipsCopy,
  note: FlightFilterNote,
  values: Readonly<Record<string, string>> | undefined,
): string {
  if (note === 'cabin' || note === 'cabinSame') return copy.note[note](values?.cabin ?? '');
  return copy.note[note];
}

function Chip({
  chip,
  names,
  copy,
  onRemove,
}: Readonly<{
  chip: FilterChip;
  names: FlightFilterChipNames;
  copy: FlightFilterChipsCopy;
  onRemove: (id: string) => void;
}>) {
  const label = chipLabel(chip, copy, names);
  const warns = chip.status === 'unsupported' || chip.status === 'unresolved';
  return (
    <li className={`ffc-chip is-${chip.status}${chip.deferred ? ' is-deferred' : ''}`}>
      {chip.status === 'pending' && <CircleNotchIcon size={12} className="ffc-spin" aria-hidden />}
      {warns && <WarningCircleIcon size={12} aria-hidden />}
      <span className="ffc-chip-label">{label}</span>
      {chip.status !== 'pending' && (
        <button
          type="button"
          className="ffc-chip-remove"
          aria-label={copy.remove(label)}
          onClick={() => {
            onRemove(chip.id);
          }}
        >
          <XIcon size={12} aria-hidden />
        </button>
      )}
      {warns && chip.reasonCode && (
        <span className="ffc-chip-reason">{copy.reason[chip.reasonCode]}</span>
      )}
    </li>
  );
}

/** Chips + nota + composer "Filtre do seu jeito": a variante de filtro do composer do DS
 *  (`FilterComposer`) com os chips do voo como slot. Vazio é a pílula; com chips ou nota
 *  vira o card (Figma 1060:23011). Não se posiciona: é o `filtersFooter` do
 *  `ResultsLayout` na coluna e o `footer` da `ResultsFiltersSheet` no estreito. */
export function FlightFilterChips({
  chips,
  note,
  noteValues,
  busy,
  disabled,
  names,
  copy,
  dictation,
  onSubmit,
  onRemove,
}: Readonly<FlightFilterChipsProps>) {
  // O aviso do consumidor: o pedido em voo vence a nota da última resposta.
  const noteOf = (): { text: string } | undefined => {
    if (busy) return { text: copy.applying };
    return note ? { text: noteText(copy, note, noteValues) } : undefined;
  };
  return (
    <FilterComposer
      busy={busy}
      disabled={disabled}
      dictation={dictation}
      onSubmit={onSubmit}
      maxLength={300}
      note={noteOf()}
      chips={
        chips.length > 0 ? (
          <ul className="ffc-chips">
            {chips.map((chip) => (
              <Chip key={chip.id} chip={chip} names={names} copy={copy} onRemove={onRemove} />
            ))}
          </ul>
        ) : undefined
      }
      labels={{
        input: copy.placeholder,
        send: copy.send,
        // Sem ditado no host o mic não é desenhado, nem como vitrine.
        dictate: dictation ? copy.dictate : undefined,
        dictationCancel: copy.dictationCancel,
        dictationStop: copy.dictationStop,
        dictationSend: copy.dictationSend,
        transcribing: copy.transcribing,
        dictationFailed: copy.dictationFailed,
      }}
    />
  );
}
